import { useEffect, useState } from "react";
import {
  Menu,
  UnstyledButton,
  Group,
  Text,
  Avatar,
  Stack,
  Loader,
  Badge,
  Divider,
} from "@mantine/core";
import {
  IconChevronDown,
  IconBrandAbstract,
  IconPlus,
  IconCheck,
} from "@tabler/icons-react";

import { useBrandContext } from "./brandContext";
import CreateBrand from "./CreateBrand";
import classes from "./BrandSwitcher.module.css";
import BrandLogo from "../_Shared/BrandLogo";

export function BrandSwitcher() {
  const [menuOpened, setMenuOpened] = useState(false);
  const [createModalOpened, setCreateModalOpened] = useState(false);

  const { selectedBrand, brands, isLoading, fetchBrands, setSelectedBrand } =
    useBrandContext();

  // Fetch brands on mount
  useEffect(() => {
    if (brands.length === 0 && !isLoading) {
      fetchBrands();
    }
  }, []);

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    setMenuOpened(false);
  };

  const handleCreateBrand = () => {
    setMenuOpened(false);
    setCreateModalOpened(true);
  };

  // Show loading state while fetching
  if (isLoading && brands.length === 0) {
    return (
      <Group gap="xs">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading brands...
        </Text>
      </Group>
    );
  }

  // Show message if no brands
  if (!isLoading && brands.length === 0) {
    return (
      <>
        <UnstyledButton onClick={handleCreateBrand} className={classes.button}>
          <Group gap="xs">
            <Avatar size={32} radius="xl" color="blue">
              <IconBrandAbstract size={32} stroke={1.5} />
            </Avatar>
            <Stack gap={0}>
              <Text size="sm" fw={500}>
                No Brands
              </Text>
              <Text size="xs" c="dimmed">
                Create your first brand
              </Text>
            </Stack>
            <IconPlus size={16} stroke={1.5} />
          </Group>
        </UnstyledButton>
        <CreateBrand
          modalOpened={createModalOpened}
          setModalOpened={setCreateModalOpened}
        />
      </>
    );
  }

  // Display selected brand or default
  const displayBrand = selectedBrand || brands[0];

  return (
    <>
      <Menu
        width={260}
        position="bottom-start"
        opened={menuOpened}
        onChange={setMenuOpened}
        withinPortal
      >
        <Menu.Target>
          <UnstyledButton className={classes.button}>
            <Group gap="xs" wrap="nowrap">
              <BrandLogo brand={displayBrand} size={48} />

              <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={500} lineClamp={1}>
                  {displayBrand?.name || "Select Brand"}
                </Text>
                {displayBrand?.websiteUrl && (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {displayBrand.websiteUrl.replace(/^https?:\/\//, "")}
                  </Text>
                )}
              </Stack>

              <IconChevronDown
                size={16}
                stroke={1.5}
                className={classes.chevron}
                data-opened={menuOpened}
                style={{ flexShrink: 0 }}
              />
            </Group>
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>Your Brands ({brands.length})</Menu.Label>

          {brands.map((brand) => (
            <Menu.Item
              key={brand.id}
              onClick={() => handleBrandSelect(brand)}
              leftSection={<BrandLogo brand={brand} size={48} />}
              rightSection={
                selectedBrand?.id === brand.id && (
                  <IconCheck size={16} stroke={2} />
                )
              }
              className={
                selectedBrand?.id === brand.id ? classes.activeItem : undefined
              }
            >
              <Stack gap={0}>
                <Group gap="xs" justify="space-between">
                  <Text size="sm" fw={500} lineClamp={1}>
                    {brand.name}
                  </Text>
                  {brand.setupStatus === "Active" && (
                    <Badge size="xs" color="green" variant="dot">
                      Active
                    </Badge>
                  )}
                </Group>
                {brand.websiteUrl && (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {brand.websiteUrl.replace(/^https?:\/\//, "")}
                  </Text>
                )}
              </Stack>
            </Menu.Item>
          ))}

          <Divider my="xs" />

          <Menu.Item
            leftSection={<IconPlus size={16} stroke={1.5} />}
            onClick={handleCreateBrand}
          >
            <Text size="sm" fw={500}>
              Create New Brand
            </Text>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      {/* Create Brand Modal */}
      <CreateBrand
        modalOpened={createModalOpened}
        setModalOpened={setCreateModalOpened}
      />
    </>
  );
}
